import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Package, TrendingUp } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article9 = () => {
  const { t, language } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.backToHome')}
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge className="bg-green-600 text-white mr-4">
              <Package className="h-3 w-3 mr-1" />
              Last Mile
            </Badge>
            <span className="text-sm text-gray-600">EcommerceNews.eu</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            February 2026
            <span className="mx-2">•</span>
            <span>5 min read</span>
          </div>
          
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.9.title')}
          </h1>
          
          <img 
            src="https://ecommercenews.eu/static/wp-content/uploads/2021/08/inpost_locker.jpg?w=360&h=240&bg=white&fm=webp" 
            alt="InPost Parcel Locker"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.9.intro')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.9.section1.title')}</h2>
            
            <p className="mb-6">
              {t('article.9.p1')}
            </p>

            <p className="mb-6">
              {t('article.9.p2')}
            </p>

            <div className="bg-green-50 p-6 rounded-lg my-8 border border-green-100">
              <h3 className="font-bold text-green-900 mb-2 flex items-center">
                <TrendingUp className="mr-2" />
                {t('article.9.highlight.title')}
              </h3>
              <p className="text-sm text-green-800">
                {t('article.9.highlight.content')}
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.9.section2.title')}</h2>
            
            <p className="mb-6">
              {t('article.9.p3')}
            </p>

            <p className="mb-6">
              {t('article.9.p4')}
            </p>

            <div className="bg-blue-50 p-6 rounded-lg my-8">
              <h3 className="font-bold text-blue-900 mb-4">{language === 'ko' ? '영국 택배 보관함 사용 통계' : 'UK Parcel Locker Usage Statistics'}</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Package className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '소비자 사용 패턴' : 'Consumer Usage Patterns'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '수령:' : 'Receiving:'}</strong> {language === 'ko' ? '41% 소비자 사용' : '41% of consumers use'}</li>
                    <li><strong>{language === 'ko' ? '발송:' : 'Sending:'}</strong> {language === 'ko' ? '44% 소비자 사용' : '44% of consumers use'}</li>
                    <li><strong>{language === 'ko' ? '문제 경험:' : 'Delivery Issues:'}</strong> {language === 'ko' ? '1/3 이상이 문제 경험' : '1/3+ experienced issues'}</li>
                    <li><strong>{language === 'ko' ? '네트워크:' : 'Network:'}</strong> {language === 'ko' ? 'InPost 13,000개 보관함' : 'InPost 13,000 lockers'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <TrendingUp className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '추가 구매 영향' : 'Additional Purchase Impact'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '보관함 방문:' : 'Locker Visits:'}</strong> {language === 'ko' ? '52% 매장 방문 이유' : '52% reason for store visits'}</li>
                    <li><strong>{language === 'ko' ? '추가 구매:' : 'Additional Purchases:'}</strong> {language === 'ko' ? '78% 방문 시 추가 구매' : '78% make additional purchases'}</li>
                    <li><strong>{language === 'ko' ? '소매 통합:' : 'Retail Integration:'}</strong> {language === 'ko' ? '매장 내 또는 근처 설치' : 'Located in or near stores'}</li>
                    <li><strong>{language === 'ko' ? '상호 이익:' : 'Mutual Benefits:'}</strong> {language === 'ko' ? '모든 파트너에게 가치' : 'Value for all partners'}</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.9.section3.title')}</h2>
            
            <p className="mb-6">
              {t('article.9.p5')}
            </p>

            <p className="mb-6">
              {t('article.9.p6')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.9.section4.title')}</h2>
            
            <p className="mb-6">
              {t('article.9.p7')}
            </p>

            <p className="mb-6">
              {t('article.9.p8')}
            </p>

            <div className="bg-orange-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-orange-900 mb-4">{language === 'ko' ? '시장 영향과 미래 전망' : 'Market Impact and Future Outlook'}</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-orange-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <TrendingUp className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '배송 신뢰성 문제' : 'Delivery Reliability Issues'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '배송 실패:' : 'Missed Deliveries:'}</strong> {language === 'ko' ? '주요 문제 중 하나' : 'Major issue identified'}</li>
                    <li><strong>{language === 'ko' ? '분실 패키지:' : 'Lost Packages:'}</strong> {language === 'ko' ? '소비자 불만 증가' : 'Growing consumer concern'}</li>
                    <li><strong>{language === 'ko' ? '택배 도난:' : 'Parcel Theft:'}</strong> {language === 'ko' ? '보안 우려 사항' : 'Security concerns'}</li>
                    <li><strong>{language === 'ko' ? '대안 필요:' : 'Alternative Needed:'}</strong> {language === 'ko' ? '신뢰할 수 있는 배송 방법' : 'Reliable delivery method'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Package className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '보관함 네트워크 확장' : 'Locker Network Expansion'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '빠른 확장:' : 'Rapid Expansion:'}</strong> {language === 'ko' ? 'InPost UK 네트워크 성장' : 'InPost UK network growth'}</li>
                    <li><strong>{language === 'ko' ? '현대 생활:' : 'Modern Life:'}</strong> {language === 'ko' ? '현대 생활 속도에 맞춤' : 'Keeping pace with modern life'}</li>
                    <li><strong>{language === 'ko' ? '소비자 조사:' : 'Consumer Survey:'}</strong> {language === 'ko' ? '2,000명 영국 소비자 대상' : '2,000 UK consumers surveyed'}</li>
                    <li><strong>{language === 'ko' ? '미래 역할:' : 'Future Role:'}</strong> {language === 'ko' ? '전자상거래 생태계 핵심' : 'Key in e-commerce ecosystem'}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} EcommerceNews.eu - 41% of UK Consumers Now Use Parcel Lockers.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a 
              href="https://ecommercenews.eu/41-uk-consumers-use-parcel-lockers/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-green-600 hover:bg-green-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToAllArticles')}
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article9;